import { inject, Injectable, OnDestroy, untracked } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  filter,
  Observable,
  Subject,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { UserStore } from '../app-state/user.store';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NotificationService } from '../services/notification.service';
import { AuthService } from '../services/auth/auth.service';
import { AuthTeardownService } from '../services/auth/auth-teardown.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
  userStore = inject(UserStore);
  router = inject(Router);
  notification = inject(NzNotificationService);
  notificationService = inject(NotificationService);
  authService = inject(AuthService);
  private readonly authTeardown = inject(AuthTeardownService);
  private isRefreshing = false;
  private readonly refreshTokenSubject: Subject<string | null> = new Subject<
    string | null
  >();
  private readonly destroy$ = new Subject<void>();
  organization = this.userStore.organization;
  organizationUser = this.userStore.organizationUser;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.refreshTokenSubject.complete();
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!request.url.startsWith(environment.api_host)) {
      return next.handle(request);
    }

    // Patient questionnaire is standalone — do not attach app auth or org headers
    if (this.router.url.includes('patient-questionnaire')) {
      return next
        .handle(request)
        .pipe(catchError((error: any) => throwError(() => error)));
    }

    // Read untracked: a request subscribed inside an effect runs this
    // synchronously, and a tracked read would make that effect re-run on
    // every token refresh (every 10 minutes), reloading whatever it loads.
    const { token, organizationId, organizationUserId } = untracked(() => ({
      token: this.userStore.token(),
      organizationId: this.organization()?.id,
      organizationUserId: this.organizationUser()?.id,
    }));
    const access_token: string | null = token || localStorage.getItem('token');

    if (access_token) {
      request = this.addToken(request, access_token);
    }

    const headers: { [key: string]: string } = {};
    if (organizationId) {
      headers['x-organization-id'] = organizationId;
    }
    if (organizationUserId) {
      headers['x-organization-user-id'] = organizationUserId;
    }

    const newRequest = request.clone({
      setHeaders: headers,
    });

    return next.handle(newRequest).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          const { status, error: errBody } = error;

          // Handle 401 and 403 errors in unified auth flow
          const isAuthError =
            status === 401 ||
            (status === 403 &&
              [
                'USER_ACCOUNT_INACTIVE',
                'EMAIL_NOT_VERIFIED',
                'ORG_MISMATCH',
                'ORG_NOT_FOUND',
                'ORG_API_KEY_MISSING',
              ].includes(errBody?.errorCode));

          // 401 = Unauthorized (invalid token, header missing, etc.)
          if (isAuthError && !request.url.includes('auth/refresh')) {
            // Don’t handle login endpoints — they have their own error handling
            if (
              request.url.includes('/auth/login') ||
              request.url.includes('/auth/login/verify') ||
              request.url.includes('/auth/emailotp/verify') ||
              request.url.includes('auth/send-email-otp') ||
              request.url.includes('auth/setup-email-otp')
            ) {
              return throwError(() => error);
            }

            // Try token refresh; if that fails, show notification and redirect
            return this.handle401Error(request, next);
          }

          // 403 = Forbidden (inactive user, no permission)
          // if (status === 403) {
          //   this.showAuthErrorNotification(errBody, 'Forbidden');
          // }

          // Other errors (network, 500, etc.)
          // if (
          //   status >= 400 &&
          //   status < 600 &&
          //   status !== 401 &&
          //   status !== 403
          // ) {
          //   this.showAuthErrorNotification(errBody);
          // }
        }

        return throwError(() => error);
      }),
    );
  }

  // -----------------
  // Notification logic
  // -----------------
  private showAuthErrorNotification(
    errBody: any,
    defaultTitle = 'Error',
  ): void {
    const errorCode = errBody?.errorCode ?? null;
    let title = defaultTitle;
    let desc: string;
    let type: 'success' | 'error' | 'warning' | 'info' = 'error';
    let key = 'auth-error';

    const friendlyFallback =
      'An unexpected error occurred. Please try again, or contact support if the problem persists.';

    switch (errorCode) {
      case 'AUTH_HEADER_MISSING':
      case 'AUTH_HEADER_INVALID_FORMAT':
      case 'TOKEN_MISSING':
      case 'TOKEN_VERIFICATION_FAILED':
      case 'TOKEN_INACTIVE':
      case 'TOKEN_VALIDATION_ERROR':
        // Session / auth issues — keep message simple and actionable
        title = 'Session Required';
        desc = 'Your session has ended or is invalid. Please sign in again.';
        break;

      case 'EMAIL_NOT_VERIFIED':
        type = 'warning';
        title = 'Email Not Verified';
        desc = 'Please verify your email to continue.';
        break;

      case 'USER_ACCOUNT_INACTIVE':
        type = 'warning';
        title = 'Account Inactive';
        desc = 'Your account is not active. Please contact support for help.';
        break;

      case 'ORG_NOT_FOUND':
        title = 'Organization Not Found';
        desc = 'We could not find the organization linked to your account.';
        break;

      case 'ORG_API_KEY_MISSING':
      case 'INVALID_API_KEY':
        title = 'Configuration Issue';
        desc = 'There is a configuration problem. Please contact support.';
        break;

      case 'ORG_MISMATCH':
        title = 'Organization Mismatch';
        desc = 'Your account does not belong to this organization.';
        break;

      default:
        // Avoid exposing raw server messages (may contain technical details).
        desc = friendlyFallback;
    }

    this.notificationService.create(type, title, desc, key, {
      nzDuration: 0,
    });
  }

  resetAndRedirect(skipNotification = false) {
    this.authTeardown.clearAuthentication({ isAuthError: true });
    localStorage.clear();
    sessionStorage.clear();
    const returnUrl = globalThis.location.pathname;
    const queryParams = returnUrl.includes('/auth')
      ? {}
      : {
          returnUrl: encodeURIComponent(
            environment.production
              ? returnUrl.replace('provider', '')
              : returnUrl,
          ),
        };

    // Redirect to login page
    this.router.navigate(['/auth/login'], { queryParams }).then();

    // Skip notification if already on the login page or explicitly suppressed
    if (skipNotification || returnUrl.includes('/auth')) return;

    // Show notification if user was logged in and session expired
    this.notificationService.create(
      'error',
      'Error',
      'Session expired. Please login again',
      'session-expired',
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshStandalone().pipe(
        switchMap((resp) => {
          this.isRefreshing = false;
          const token = resp?.data?.token;
          if (token) {
            localStorage.setItem('token', token);
            this.userStore.setToken(token);
            this.refreshTokenSubject.next(token);
            return next.handle(this.addToken(request, token));
          }
          // No token in refresh response → force logout
          this.showAuthErrorNotification(
            {
              errorCode: 'TOKEN_VERIFICATION_FAILED',
              message: 'Session expired.',
            },
            'Unauthorized',
          );
          this.resetAndRedirect(true);
          return throwError(() => new Error('Unauthorized'));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.showAuthErrorNotification(
            {
              errorCode: 'TOKEN_VERIFICATION_FAILED',
              message: 'Session expired.',
            },
            'Unauthorized',
          );
          this.resetAndRedirect(true);
          return throwError(() => error);
        }),
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) =>
        next.handle(this.addToken(request, token as string)),
      ),
    );
  }
}
